<script setup lang="ts">
import { storeToRefs } from 'pinia';

import { ClovyrLogo } from '@clovyr/bed';
import Tooltip from '@clovyr/bed/components/blocks/Tooltip.vue';
import CLink from '@clovyr/bed/components/elements/CLink.vue';
import { AuthState } from '@clovyr/pollen/pollen/types';

import ArrowIcon from '@/components/icons/ArrowIcon.vue';
import ClovyrIcon from '@/components/icons/ClovyrIcon.vue';
import DotsHorizontalIcon from '@/components/icons/DotsHorizontalIcon.vue';
import GardenIcon from '@/components/icons/GardenIcon.vue';
import LibraryIcon from '@/components/icons/LibraryIcon.vue';
import LockIcon from '@/components/icons/LockIcon.vue';
import ProfileIcon from '@/components/icons/ProfileIcon.vue';
import PublisherIcon from '@/components/icons/PublisherIcon.vue';
import SlideIcon from '@/components/icons/SlideIcon.vue';
import { useInstanceSleepMonitor } from '@/composables/useInstanceSleepMonitor';
import MobileNavbar from '@/global/Sidebar/MobileNavbar.vue';
import { useEventBus } from '@/stores/event_bus';
import { useMediaQueryStore } from '@/stores/media_query_store';
import { usePollenStore } from '@/stores/pollen_store';
import { useUIStateStore } from '@/stores/ui_state_store';
import { useUserFlagsStore } from '@/stores/user_flags';
import Help from '@/views/Help/Help.vue';
import Settings from '@/views/Settings/Settings.vue';
import { ShowAuthForm } from '@/views/Settings/types';
import SideDrawer from '@/views/SideDrawer.vue';

import NavAppList from './Sidebar/NavAppList.vue';
import Modals from './Modals.vue';

const options = {
  Settings,
  Help,
};

const { eventBus } = useEventBus();
const uiStateStore = useUIStateStore();
const { sidenavIsCollapsed: sidenavIsCollapsedStore } = storeToRefs(uiStateStore);
const pollenStore = usePollenStore();
const { deployments, sortedDeployments, isUserAuthenticated, isPublisher, authState } =
  storeToRefs(pollenStore);
const route = useRoute();

const selectedOption = ref('');
const { isMobile } = storeToRefs(useMediaQueryStore());
const mobileNavBarOpen = ref(false);

// Local ref which does not update localstorage state when changed
const localCollapsed = ref<boolean>(true);

const useLocalCollapsed = computed(() => {
  return route.name === 'ViewDeployment';
});

const sidebar = ref<HTMLElement | null>(null);

const { canAccessPreviewFeatures } = storeToRefs(useUserFlagsStore());

// Use a writable computed so that we can avoid saving state when on the FramedDeployment view
// (clovyr code)
const sidenavIsCollapsed = computed({
  get() {
    if (useLocalCollapsed.value) {
      return localCollapsed.value;
    }
    return sidenavIsCollapsedStore.value;
  },
  set(newValue) {
    if (useLocalCollapsed.value) {
      localCollapsed.value = newValue;
    } else {
      sidenavIsCollapsedStore.value = newValue;
    }
  },
});

/**
 * Should be called whenever the sidebar changes state
 */
if (!import.meta.env.SSR) {
  watchEffect(() => {
    let w = `var(--sidebar-width-${sidenavIsCollapsed.value ? 'closed' : 'open'})`;
    if (isMobile.value) {
      w = '0px';
    }
    document.documentElement.style.setProperty('--sidebar-current-width', w);
  });
}

const handleSidebarToggle = () => {
  sidenavIsCollapsed.value = !sidenavIsCollapsed.value;
  selectedOption.value = '';
};

const menuItemIsSelected = (view) => {
  if (selectedOption.value === view) {
    return 'sidebar-menu__item--selected';
  }

  return undefined;
};

const handleDrawerClose = () => {
  selectedOption.value = '';
};

const onClickCloseDrawer = () => {
  handleDrawerClose();
  window.location.hash = '';
};

const stateClass = computed(() => {
  return `sidebar--${sidenavIsCollapsed.value ? 'closed' : 'open'}`;
});

const libToggled = ref(false);

const handleLibToggle = () => {
  libToggled.value = !libToggled.value;
};

const libArrowClassNames = computed(() => {
  return [libToggled.value && 'button--toggled'];
});

const libClassNames = computed(() => {
  return [
    deployments.value.length > 0 && 'app-library-listing',
    libToggled.value && 'app-library-expanded',
  ];
});

const toggleBodyLockClass = () => {
  if (document.body.classList.contains('body-locked')) {
    document.body.classList.remove('body-locked');
  } else {
    document.body.classList.add('body-locked');
  }
};

const handleDrawerToggle = (view) => {
  if (mobileNavBarOpen.value) {
    mobileNavBarOpen.value = false;
  }

  if (selectedOption.value === view) {
    selectedOption.value = '';
  } else {
    selectedOption.value = view;
  }

  toggleBodyLockClass();
};

const checkHashForSettings = () => {
  if (window.location.hash) {
    const hash = window.location.hash.slice(1);
    if (hash.split('/').includes('settings')) {
      selectedOption.value = 'Settings';
    }
  }
};

onMounted(() => {
  useInstanceSleepMonitor();
  eventBus.on('drawer:close', handleDrawerClose);
  checkHashForSettings();
  window.addEventListener('hashchange', checkHashForSettings, false);
  void nextTick(() => {
    if (sidebar.value) {
      // A hydration mismatch where only the class (or other?) attributes differ does not trigger a
      // re-render of the elements. Only structural changes do. To workaround this, we force the
      // class to be updated here.
      // see note in this PR: https://github.com/vuejs/core/pull/5953
      sidebar.value.classList.remove('sidebar--open', 'sidebar--closed');
      sidebar.value.classList.add(stateClass.value);
    }
  });
});

onUnmounted(() => {
  // actually this will never fire? maybe if we switch to mobile nav?
  eventBus.off('drawer:close', handleDrawerClose);
  window.removeEventListener('hashchange', checkHashForSettings, false);
});

function toggleDrawer(option) {
  handleDrawerToggle(option);
}

function toggleMobileNavbar() {
  mobileNavBarOpen.value = !mobileNavBarOpen.value;
}

// Define computed property to determine if any publisher-related route is active
const isPublisherRouteActive = computed(() => {
  return {
    'router-link-active': route.path.startsWith('/publishers'),
  };
});
</script>

<template>
  <div :class="stateClass" class="sidebar" ref="sidebar">
    <div class="sidebar__inner">
      <div class="sidebar__backdrop" />

      <div class="sidebar__top">
        <div class="sidebar-logo">
          <div v-show="sidenavIsCollapsed" class="sidebar-logo--abbreviated">
            <router-link to="/">
              <ClovyrIcon />
            </router-link>
          </div>

          <div v-show="!sidenavIsCollapsed" class="sidebar-logo--full">
            <router-link to="/">
              <ClovyrLogo />
            </router-link>
          </div>
        </div>

        <div :class="libClassNames" class="app-library" data-allow-mismatch>
          <div class="app-library-heading">
            <router-link :to="{ name: 'Library' }">
              <span class="app-library-heading-label">
                <span class="app-library-heading__icon">
                  <Tooltip
                    :disabled="!sidenavIsCollapsed"
                    placement="right"
                    theme="narrow"
                    :arrow="false"
                    content="Library"
                  >
                    <LibraryIcon />
                  </Tooltip>
                </span>
                <span class="app-library-heading__title">Library</span>
              </span>
            </router-link>

            <span v-if="sortedDeployments.length > 5" class="app-library-arrow">
              <button
                @click="handleLibToggle"
                :class="libArrowClassNames"
                class="button--action button--toggle"
              >
                <Tooltip
                  :disabled="!sidenavIsCollapsed"
                  placement="right"
                  theme="narrow"
                  :arrow="false"
                  content="Collapse"
                >
                  <ArrowIcon :direction="libToggled ? 'top' : 'down'" />
                </Tooltip>
              </button>
            </span>
          </div>

          <NavAppList :closed="sidenavIsCollapsed" :library-toggled="!libToggled" />
        </div>

        <ul class="sidebar-navigation" data-allow-mismatch="children">
          <li class="navigation-item">
            <router-link :to="{ name: 'Garden' }">
              <span class="navigation-item__icon">
                <Tooltip
                  :disabled="!sidenavIsCollapsed"
                  placement="right"
                  theme="narrow"
                  :arrow="false"
                  content="Garden"
                >
                  <GardenIcon />
                </Tooltip>
              </span>
              <span class="navigation-item__title">Garden</span>
            </router-link>
          </li>
          <li
            class="navigation-item"
            :class="isPublisherRouteActive"
            v-if="isPublisher || canAccessPreviewFeatures"
            data-allow-mismatch
          >
            <router-link :to="{ name: 'Publishers' }">
              <span class="navigation-item__icon">
                <Tooltip
                  :disabled="!sidenavIsCollapsed"
                  placement="right"
                  theme="narrow"
                  :arrow="false"
                  content="Publishers"
                >
                  <PublisherIcon />
                </Tooltip>
              </span>
              <span class="navigation-item__title">Publishers</span>
            </router-link>
          </li>
          <li
            v-if="!isUserAuthenticated"
            class="navigation-item"
            @click="eventBus.emit('modal:login:show')"
            data-allow-mismatch
          >
            <span class="navigation-item__icon" data-allow-mismatch>
              <Tooltip
                :disabled="!sidenavIsCollapsed"
                placement="right"
                theme="narrow"
                :arrow="false"
                content="Log In / Sign Up"
              >
                <ProfileIcon />
              </Tooltip>
            </span>
            <span class="navigation-item__title">Log In</span>
          </li>
          <li
            v-if="isUserAuthenticated && authState === AuthState.Locked"
            class="navigation-item"
            @click="eventBus.emit('modal:login:show', { form: ShowAuthForm.Locked })"
          >
            <span class="navigation-item__icon">
              <Tooltip
                :disabled="!sidenavIsCollapsed"
                placement="right"
                theme="narrow"
                :arrow="false"
                content="Your account is locked. Click here to unlock it."
              >
                <LockIcon />
              </Tooltip>
            </span>
            <span class="navigation-item__title">Unlock</span>
          </li>
        </ul>
      </div>

      <div class="sidebar-bottom">
        <div class="sidebar-bottom__inner">
          <!-- Expanded menu items -->
          <ul v-show="!sidenavIsCollapsed" class="sidebar-menu">
            <li
              :class="menuItemIsSelected('Settings')"
              @click="handleDrawerToggle('Settings')"
              class="sidebar-menu__item settings"
              data-allow-mismatch
            >
              <span v-if="isUserAuthenticated" class="icon">
                <ProfileIcon />
              </span>
              <span class="title">Settings</span>
            </li>
            <li class="sidebar-menu__item">
              <c-link to="https://help.clovyr.io/" label="Help" target="_blank" />
            </li>
            <li class="sidebar-menu__item">
              <c-link to="https://clovyr.io" label="Clovyr.io" target="_blank" />
            </li>
            <li class="sidebar-menu__item">
              <RouterLink to="/privacy-policy">Privacy Policy</RouterLink>
            </li>
          </ul>

          <!-- Collapsed menu items -->
          <Tooltip
            v-if="sidenavIsCollapsed"
            placement="right"
            theme="narrow sidebar-menu-options"
            :arrow="false"
            class="tooltip-nav"
          >
            <DotsHorizontalIcon />

            <template #content>
              <ul class="sidebar-menu">
                <li
                  :class="menuItemIsSelected('Settings')"
                  @click="handleDrawerToggle('Settings')"
                  class="sidebar-menu__item"
                  data-allow-mismatch
                >
                  <span class="title">Settings</span>
                </li>
                <li class="sidebar-menu__item">
                  <c-link to="https://help.clovyr.io/" label="Help" target="_blank" />
                </li>
                <li class="sidebar-menu__item">
                  <a href="https://clovyr.io" target="_blank">Clovyr.io</a>
                </li>
                <li class="sidebar-menu__item">
                  <RouterLink to="/privacy-policy">Privacy Policy</RouterLink>
                </li>
              </ul>
            </template>
          </Tooltip>

          <div @click="handleSidebarToggle" class="slide-icon">
            <Tooltip
              :disabled="!sidenavIsCollapsed"
              placement="right"
              theme="narrow"
              :arrow="false"
              content="Expand"
            >
              <SlideIcon />
            </Tooltip>
          </div>
        </div>
      </div>

      <Modals />

      <SideDrawer v-if="selectedOption" @close="onClickCloseDrawer">
        <component :is="options[selectedOption]" />
      </SideDrawer>
    </div>
  </div>

  <MobileNavbar
    v-if="isMobile"
    @toggle-drawer="toggleDrawer"
    @toggle-mobile-navbar="toggleMobileNavbar"
    :mobile-navBar-open="mobileNavBarOpen"
  />
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';

:deep(svg) {
  margin: unset !important;
}

.sidebar--closed {
  :deep(.app-library-item__title-abbreviated) {
    padding-left: grid-vars.space(2.25);
  }
  .app-library {
    &__items {
      padding-left: grid-vars.space(1.5) !important;
    }
  }
}

.sidebar--open {
  :deep(.app-library-item__title-abbreviated) {
    padding-left: grid-vars.space(0.5);
  }
  .app-library {
    &__items {
      padding-left: grid-vars.space(2.5) !important;
    }
  }

  :deep(.slide) {
    transform: rotate(180deg);
  }
}
.navigation-item:hover {
  color: colors.color(action, primary) !important;
  :deep(.publisher-icon) {
    path {
      fill: colors.color(action, primary);
    }
  }
}
.router-link-active {
  .navigation-item__title {
    color: colors.color(action, secondary);
  }
  .navigation-item__icon {
    :deep(.publisher-icon) {
      path {
        fill: colors.color(action, secondary);
      }
    }
  }
}
.router-link-active:hover {
  .navigation-item__title {
    color: colors.color(action, primary);
  }
  .navigation-item__icon {
    :deep(.publisher-icon) {
      path {
        fill: colors.color(action, primary);
      }
    }
  }
}
</style>
